import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUsers,
  FaFileAlt,
  FaFilter,
  FaSearch,
  FaUserFriends,
  FaUserCheck,
  FaClipboardCheck,
  FaHeadset,
  FaChartBar,
  FaPhone,
  FaPhoneAlt,
  FaCheckCircle,
  FaBan,
  FaArrowUp,
  FaArrowDown,
  FaChevronRight
} from 'react-icons/fa';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import api from '../../service/api';
import { useToast } from '../../contexts/ToastContext';
import './Dashboard.css';

const emptyData = {
  filters: { assemblies: [], telecallers: [], questions: [] },
  kpis: {},
  funnel: [],
  outreachTrend: [],
  issuePriorities: null,
  choiceQuestions: [],
  questionAnalytics: [],
  topTelecallers: [],
  geography: []
};

const KPI_META = [
  {
    key: 'totalVoters',
    title: 'Total Voters',
    icon: FaUserFriends,
    tone: 'blue'
  },
  {
    key: 'assigned',
    title: 'Assigned for Current Campaign',
    icon: FaUserCheck,
    tone: 'green'
  },
  {
    key: 'surveysCompleted',
    title: 'Surveys Completed',
    icon: FaClipboardCheck,
    tone: 'purple'
  },
  {
    key: 'responseRate',
    title: 'Response Rate',
    icon: FaChartBar,
    tone: 'yellow',
    suffix: '%'
  },
  {
    key: 'activeTelecallers',
    title: 'Active Telecallers',
    icon: FaHeadset,
    tone: 'sky'
  }
];

const ANALYTIC_TONES = ['blue', 'green', 'purple', 'orange'];
const FUNNEL_STAGE_META = [
  { key: 'assigned', label: 'Assigned', icon: FaUsers, tone: 'assigned' },
  { key: 'contacted', label: 'Contacted', icon: FaPhone, tone: 'contacted' },
  { key: 'completed', label: 'Completed', icon: FaCheckCircle, tone: 'completed' },
  { key: 'callback', label: 'Callback', icon: FaPhoneAlt, tone: 'callback' },
  { key: 'unreachable', label: 'Unreachable', icon: FaBan, tone: 'unreachable' }
];

const formatNumber = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '0';
  return Number(value).toLocaleString();
};

const KpiCard = ({ title, icon: Icon, tone, value, suffix, trendPct, trendLabel }) => {
  const trendClass = trendPct == null ? 'neutral' : trendPct >= 0 ? 'up' : 'down';

  return (
    <Card className="campaign-card campaign-kpi-card h-100 shadow-sm border-0">
      <Card.Body>
        <div className="campaign-kpi">
          <div className={`campaign-icon campaign-icon-${tone}`}>
            <Icon size={16} />
          </div>
          <span className="campaign-kpi-divider" aria-hidden="true" />
          <div className="campaign-kpi-copy">
            <div className="campaign-kpi-title">{title}</div>
            <div className="campaign-kpi-value">
              {value}
              {suffix || ''}
            </div>
            <div className={`campaign-trend ${trendClass}`}>
              {trendPct != null && (trendPct >= 0
                ? <FaArrowUp className="me-1" size={9} />
                : <FaArrowDown className="me-1" size={9} />)}
              {trendLabel || 'No comparison available'}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const GroupedBarChart = ({ data }) => {
  const chartData = data || [];

  return (
    <div className="grouped-chart-container">
      <div className="grouped-chart-inner">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 6, right: 4, left: -18, bottom: 0 }} barGap={3}>
            <CartesianGrid stroke="#eef2f7" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={8}
              tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={36}
              allowDecimals={false}
              tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
              tickFormatter={formatNumber}
            />
            <Tooltip
              cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }}
              formatter={(value, name) => [formatNumber(value), name === 'callsMade' ? 'Calls Made' : 'Surveys Completed']}
              labelStyle={{ color: '#0f172a', fontWeight: 700 }}
              contentStyle={{
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                fontSize: 12
              }}
            />
            <Bar dataKey="callsMade" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={22} />
            <Bar dataKey="surveysCompleted" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={22} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const FunnelChart = ({ steps }) => {
  const stepsByKey = new Map((steps || []).map((step) => [step.key, step]));
  const funnelSteps = FUNNEL_STAGE_META
    .map((stage) => ({ ...stage, ...stepsByKey.get(stage.key) }))
    .filter((stage) => stage.count != null);
  if (!funnelSteps.length) return <div className="campaign-empty-state">No campaign activity for this selection.</div>;

  return (
    <div className="campaign-funnel-rows" role="list" aria-label="Campaign stages">
      {funnelSteps.map((step) => {
        const Icon = step.icon;
        const percentage = Number(step.pct) || 0;
        return (
          <div key={step.key} className={`campaign-funnel-row campaign-funnel-${step.tone}`} role="listitem">
            <span className="campaign-funnel-icon" aria-hidden="true"><Icon /></span>
            <span className="campaign-funnel-stage">{step.label}</span>
            <strong className="campaign-funnel-count">{formatNumber(step.count)}</strong>
            <span className="campaign-funnel-track" aria-hidden="true">
              <span className="campaign-funnel-fill" style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }} />
            </span>
            <span className="campaign-funnel-pct">{percentage}%</span>
          </div>
        );
      })}
    </div>
  );
};

const HorizontalBars = ({ items, valueKey = 'pct', labelKey = 'label', color = '#2563eb', formatValue, showPct, emptyMessage = 'No data for this selection.' }) => {
  const dataItems = items || [];
  if (dataItems.length === 0) return <div className="campaign-empty-state">{emptyMessage}</div>;
  const max = Math.max(1, ...dataItems.map((item) => item[valueKey] || item.count || item.pct || 0));

  return (
    <div className="hbar-list">
      {dataItems.map((item, idx) => {
        const val = item[valueKey] != null ? item[valueKey] : (item.pct || item.count || 0);
        const fillWidth = Math.min(100, (val / max) * 100);

        return (
          <div className="hbar-row" key={item[labelKey] || item.label || idx}>
            <div className="hbar-name" title={item[labelKey] || item.label}>
              {item[labelKey] || item.label}
            </div>
            <div className="hbar-track">
              <div
                className="hbar-fill"
                style={{ width: `${fillWidth}%`, backgroundColor: color }}
              />
            </div>
            <div className="hbar-value">
              {showPct ? `${val}%` : (formatValue ? formatValue(val) : formatNumber(val))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const qualityClass = (score) => {
  if (score >= 80) return 'good';
  if (score >= 50) return 'mid';
  return 'low';
};

const AdminDashboard = () => {
  const [draft, setDraft] = useState({
    range: 'last7',
    acNo: '',
    telecallerId: '',
    questionId: '',
    status: 'all'
  });
  const [applied, setApplied] = useState(draft);
  const [issueQuestionId, setIssueQuestionId] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const { showError } = useToast();

  const fetchDashboard = async (filters) => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/dashboard', {
        params: {
          range: filters.range,
          acNo: filters.acNo || undefined,
          telecallerId: filters.telecallerId || undefined,
          questionId: filters.questionId || undefined,
          status: filters.status && filters.status !== 'all' ? filters.status : undefined
        }
      });
      setData(response.data);
          const selected = response.data.issuePriorities?.questionId || '';
      setIssueQuestionId(selected);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      showError(err.response?.data?.message || 'Failed to load campaign analytics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard(applied);
  }, []);

  const handleDraft = (event) => {
    const { name, value } = event.target;
    setDraft((current) => ({ ...current, [name]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setApplied(draft);
    fetchDashboard(draft);
  };

  const issueQuestion = useMemo(() => {
    const list = data.choiceQuestions || [];
    return list.find((item) => item.questionId === issueQuestionId)
      || data.issuePriorities
      || (!applied.questionId ? list[0] : null);
  }, [data, issueQuestionId, applied.questionId]);

  const kpis = data.kpis || {};
  const rangeLabel = applied.range === 'last30'
    ? 'last 30 days'
    : applied.range === 'last90'
      ? 'last 90 days'
      : applied.range === 'all'
        ? 'all time'
        : 'last 7 days';

  const qAnalytics = data.questionAnalytics || [];
  const topTelecallers = data.topTelecallers || [];
  const geography = data.geography || [];
  const selectedQuestion = useMemo(() => {
    if (!selectedQuestionId) return null;
    const detail = (data.choiceQuestions || []).find((item) => item.questionId === selectedQuestionId);
    const summary = qAnalytics.find((item) => item.questionId === selectedQuestionId);
    if (!detail && !summary) return null;
    return {
      questionId: selectedQuestionId,
      questionText: detail?.questionText || summary?.questionText || '',
      questionType: detail?.questionType || 'choice',
      total: detail?.total ?? summary?.total ?? 0,
      options: detail?.options || [],
      topAnswer: summary?.topAnswer || 'No responses yet'
    };
  }, [selectedQuestionId, data.choiceQuestions, qAnalytics]);

  return (
    <div className="campaign-dash">
      <h1 className="campaign-title">Campaign Analytics Dashboard</h1>
      <p className="campaign-subtitle">Track public opinion, monitor survey performance, and manage your telecalling campaign in real time.</p>

      <Form onSubmit={applyFilters}>
        <Card className="campaign-card mb-4 shadow-sm border-0">
          <Card.Body>
            <div className="campaign-filters-grid">
              <div className="campaign-filter-field">
                <label className="campaign-filter-label">Date Range</label>
                <div className="campaign-filter-input-wrap">
                  <FaCalendarAlt className="campaign-filter-icon" />
                  <Form.Select name="range" value={draft.range} onChange={handleDraft}>
                    <option value="last7">Last 7 Days</option>
                    <option value="last30">Last 30 Days</option>
                    <option value="last90">Last 90 Days</option>
                    <option value="all">All Time</option>
                  </Form.Select>
                </div>
              </div>

              <div className="campaign-filter-field">
                <label className="campaign-filter-label">Area / Constituency</label>
                <div className="campaign-filter-input-wrap">
                  <FaMapMarkerAlt className="campaign-filter-icon" />
                  <Form.Select name="acNo" value={draft.acNo} onChange={handleDraft}>
                    <option value="">All Areas</option>
                    {(data.filters?.assemblies || []).map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <div className="campaign-filter-field">
                <label className="campaign-filter-label">Telecaller</label>
                <div className="campaign-filter-input-wrap">
                  <FaUsers className="campaign-filter-icon" />
                  <Form.Select name="telecallerId" value={draft.telecallerId} onChange={handleDraft}>
                    <option value="">All Telecallers</option>
                    {(data.filters?.telecallers || []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}{item.isActive ? '' : ' (inactive)'}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <div className="campaign-filter-field">
                <label className="campaign-filter-label">Survey / Question</label>
                <div className="campaign-filter-input-wrap">
                  <FaFileAlt className="campaign-filter-icon" />
                  <Form.Select name="questionId" value={draft.questionId} onChange={handleDraft}>
                    <option value="">All Surveys</option>
                    {(data.filters?.questions || []).map((item) => (
                      <option key={item.id} value={item.id}>{item.questionText}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <div className="campaign-filter-field">
                <label className="campaign-filter-label">Status</label>
                <div className="campaign-filter-input-wrap">
                  <FaFilter className="campaign-filter-icon" />
                  <Form.Select name="status" value={draft.status} onChange={handleDraft}>
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="call_back">Callback</option>
                    <option value="unreachable">Unreachable</option>
                  </Form.Select>
                </div>
              </div>

              <div className="campaign-filter-action">
                <Button type="submit" className="campaign-apply w-100" disabled={loading}>
                  <FaSearch className="me-2" />
                  Apply Filters
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Form>

      {loading ? (
        <div className="d-flex align-items-center justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
          <span className="ms-3 text-secondary fw-semibold">Loading campaign analytics...</span>
        </div>
      ) : (
        <>
          <div className="campaign-kpi-grid mb-4">
            {KPI_META.map((item) => {
              const kpi = kpis[item.key] || {};
              const rawVal = kpi.value;
              const display = rawVal == null
                ? '—'
                : item.key === 'responseRate'
                ? (typeof rawVal === 'number' ? rawVal.toFixed(1) : rawVal)
                : typeof rawVal === 'number'
                  ? formatNumber(rawVal)
                  : rawVal;

              return (
                <KpiCard
                  key={item.key}
                  title={item.title}
                  icon={item.icon}
                  tone={item.tone}
                  value={display}
                  suffix={item.suffix}
                  trendPct={kpi.trendPct}
                  trendLabel={kpi.trendLabel}
                />
              );
            })}
          </div>

          <div className="campaign-panel-grid campaign-panel-grid-main mb-4">
            <div className="campaign-panel campaign-panel-chart">
              <Card className="campaign-card h-100 shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <h5 className="campaign-card-title">Outreach Trend</h5>
                      <p className="campaign-card-sub">Daily calls made vs surveys completed ({rangeLabel})</p>
                    </div>
                    <div className="campaign-legend">
                      <span><i className="dot calls" /> Calls Made</span>
                      <span><i className="dot surveys" /> Surveys Completed</span>
                    </div>
                  </div>
                  {data.outreachTrend?.length
                    ? <GroupedBarChart data={data.outreachTrend} />
                    : <div className="campaign-empty-state">No outreach activity recorded for this date range.</div>}
                </Card.Body>
              </Card>
            </div>

            <div className="campaign-panel campaign-panel-funnel">
              <Card className="campaign-card h-100 shadow-sm border-0">
                <Card.Body>
                  <div className="campaign-card-heading mb-2">
                    <div className="campaign-funnel-heading">
                      <h5 className="campaign-card-title">Campaign Funnel</h5>
                      {kpis.responseRate?.value != null && (
                        <span className="campaign-conversion">Conversion: {Number(kpis.responseRate.value).toFixed(1)}%</span>
                      )}
                    </div>
                    <p className="campaign-card-sub">Voter outreach progress for current campaign</p>
                  </div>
                  <FunnelChart steps={data.funnel} />
                </Card.Body>
              </Card>
            </div>

            <div className="campaign-panel campaign-panel-bars">
              <Card className="campaign-card h-100 shadow-sm border-0">
                <Card.Body>
                  <h5 className="campaign-card-title">Public Issue Priorities</h5>
                  <p className="campaign-card-sub mb-2">Response breakdown for one selected question</p>
                  <Form.Select
                    className="mb-2 campaign-question-select"
                    value={issueQuestionId}
                    onChange={(event) => setIssueQuestionId(event.target.value)}
                  >
                    <option value="" disabled={(data.choiceQuestions || []).length > 0}>
                      {(data.choiceQuestions || []).length > 0 ? 'Select a multiple-choice question' : 'No multiple-choice questions available'}
                    </option>
                    {(data.choiceQuestions || []).map((item, index) => (
                      <option key={item.questionId} value={item.questionId}>
                        Question {index + 1}: {item.questionText}
                      </option>
                    ))}
                  </Form.Select>
                  {issueQuestion?.total === 0 && (
                    <div className="campaign-question-empty">No responses yet</div>
                  )}
                  <HorizontalBars
                    items={issueQuestion?.options || []}
                    valueKey="pct"
                    labelKey="label"
                    color="#2563eb"
                    showPct
                    emptyMessage="No survey responses recorded for this question."
                  />
                </Card.Body>
              </Card>
            </div>
          </div>

          <div className="campaign-panel-grid campaign-panel-grid-secondary">
            <div className="campaign-panel">
              <Card className="campaign-card campaign-clip-card shadow-sm border-0">
                <Card.Body>
                  <h5 className="campaign-card-title">Multi-Question Analytics</h5>
                  <p className="campaign-card-sub mb-2">Top response across survey questions</p>
                  <div className="q-row-list campaign-clip-body">
                    {qAnalytics.map((item, index) => {
                      const toneClass = ANALYTIC_TONES[index % ANALYTIC_TONES.length];
                      const isSelected = selectedQuestionId === item.questionId;
                      const pct = Math.max(0, Math.min(100, Number(item.pct) || 0));
                      return (
                        <button
                          type="button"
                          key={item.questionId || index}
                          className={`q-row q-row-${toneClass}${isSelected ? ' is-selected' : ''}`}
                          onClick={() => setSelectedQuestionId(item.questionId)}
                        >
                          <span className="q-row-badge">Q{item.index || index + 1}</span>
                          <span className="q-row-title" title={item.questionText}>{item.questionText}</span>
                          <span className="q-row-top">
                            <span className="q-row-top-label">Top response:</span>
                            <span className="q-row-top-val">{item.topAnswer}</span>
                          </span>
                          <span className="q-row-pct">{pct}%</span>
                          <span className="q-row-progress" aria-hidden="true">
                            <span style={{ width: `${pct}%` }} />
                          </span>
                          <FaChevronRight className="q-row-chevron" aria-hidden="true" />
                        </button>
                      );
                    })}
                    {qAnalytics.length === 0 && <div className="campaign-empty-state">No multiple-choice responses recorded for this selection.</div>}
                  </div>
                </Card.Body>
              </Card>
            </div>

            <div className="campaign-panel">
              <Card className="campaign-card campaign-clip-card shadow-sm border-0">
                <Card.Body>
                  <h5 className="campaign-card-title">Top Telecaller Performance</h5>
                  <p className="campaign-card-sub mb-2">Completion rate by telecaller for the selected period</p>
                  <div className="campaign-caller-table campaign-clip-body">
                    <div className="campaign-caller-head">
                      <span className="col-rank">#</span>
                      <span className="col-name">Telecaller</span>
                      <span className="col-num">Assigned</span>
                      <span className="col-num">Completed</span>
                      <span className="col-rate">Rate</span>
                    </div>
                    {topTelecallers.map((row, idx) => (
                      <div className="campaign-caller-row" key={row.id || idx}>
                        <span className="col-rank text-secondary fw-bold">{row.rank || idx + 1}</span>
                        <span className="col-name">
                          <div className="campaign-caller-name">{row.name}</div>
                          <div className="campaign-caller-user">@{row.username}</div>
                        </span>
                        <span className="col-num">{formatNumber(row.assigned)}</span>
                        <span className="col-num">{formatNumber(row.completed)}</span>
                        <span className="col-rate">
                          <span className={`quality-pill ${qualityClass(row.quality)}`}>
                            {row.quality}%
                          </span>
                        </span>
                      </div>
                    ))}
                    {topTelecallers.length === 0 && (
                      <div className="campaign-empty-state">No telecaller activity for this selection.</div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </div>

            <div className="campaign-panel">
              <Card className="campaign-card campaign-clip-card shadow-sm border-0">
                <Card.Body>
                  <h5 className="campaign-card-title">Geographic Response Distribution</h5>
                  <p className="campaign-card-sub mb-2">Surveys completed by top constituencies</p>
                  <div className="campaign-clip-body">
                    <HorizontalBars
                      items={geography}
                      valueKey="completed"
                      labelKey="label"
                      color="#2563eb"
                      emptyMessage="No completed surveys recorded for this selection."
                    />
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </>
      )}

      <Modal
        show={Boolean(selectedQuestion)}
        onHide={() => setSelectedQuestionId('')}
        centered
        scrollable
        dialogClassName="q-detail-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Question analytics</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedQuestion && (
            <>
              <p className="q-detail-text">{selectedQuestion.questionText}</p>
              <div className="q-detail-meta">
                <span>Type: {selectedQuestion.questionType || 'N/A'}</span>
                <span>Total responses: {formatNumber(selectedQuestion.total)}</span>
              </div>
              {selectedQuestion.total === 0 ? (
                <div className="campaign-empty-state">No responses yet</div>
              ) : (
                <div className="q-detail-options">
                  {(selectedQuestion.options || []).map((option, idx) => (
                    <div className="q-detail-option" key={option.label || idx}>
                      <div className="q-detail-option-head">
                        <span className="q-detail-option-label">{option.label}</span>
                        <span className="q-detail-option-stats">
                          {formatNumber(option.count)} · {option.pct || 0}%
                        </span>
                      </div>
                      <div className="q-detail-option-track">
                        <div style={{ width: `${Math.max(0, Math.min(100, Number(option.pct) || 0))}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
